import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { FC } from "react";

import { cn } from "@/shared/lib";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";
import { SeverityDot } from "@/shared/ui/severity-dot";

import type { AnalysisSection } from "../../model/AnalysisSection";
import { CATEGORY_LABELS } from "../../model/CATEGORY_LABELS";
import { categoryAnchor } from "../../model/categoryAnchor";
import { FINDING_CATEGORIES } from "../../model/FINDING_CATEGORIES";
import type { Finding, FindingCategory } from "../../model/Finding";
import type { Report } from "../../model/Report";
import { useCategoryOverview } from "../CategoryOverview";
import { BuySection } from "./BuySection";
import { DeathsSection } from "./DeathsSection";
import { FarmSection } from "./FarmSection";
import { FightsSection } from "./FightsSection";
import { PlainSection } from "./PlainSection";
import type { SectionProps } from "./SectionProps";

const MORE = msg({ id: "analysis.section.more", message: "Details" });
const LESS = msg({ id: "analysis.section.less", message: "Collapse" });

// Содержимое секции по категории. `Record` по union из контракта: новая категория
// не соберётся, пока для неё нет секции.
const SECTIONS: Record<FindingCategory, FC<SectionProps>> = {
  buy: BuySection,
  farm: FarmSection,
  deaths: DeathsSection,
  fights: FightsSection,
  skills: PlainSection,
};

// Категории, которые показываются, только если в них есть находки: прокачку разбор почти
// не смотрит, и «ошибок не нашли» там вводит в заблуждение.
const ONLY_WITH_FINDINGS: ReadonlySet<FindingCategory> = new Set(["skills"]);

type AnalysisSectionsProps = {
  report: Report;
  // Находки, уже показанные наверху («Главное», «Ещё исправить»).
  featured: readonly Finding[];
  open: AnalysisSection;
  onOpenChange: (section: AnalysisSection) => void;
};

// Секции категорий по макету: аккордеон, открыта одна. В заголовке — оценка, название
// и итог категории, внутри — подробности (таблицы, шкалы, карточки) и находки.
export const AnalysisSections = ({
  report,
  featured,
  open,
  onOpenChange,
}: AnalysisSectionsProps) => {
  const { _ } = useLingui();
  const overviewOf = useCategoryOverview(report);
  const categories = FINDING_CATEGORIES.filter(
    (category) =>
      !ONLY_WITH_FINDINGS.has(category) ||
      report.findings.some((finding) => finding.category === category),
  );
  return (
    <Accordion
      type="single"
      collapsible
      value={open === "none" ? "" : open}
      onValueChange={(value) => {
        onOpenChange(categories.find((category) => category === value) ?? "none");
      }}
      className="flex flex-col gap-2.5"
    >
      {categories.map((category) => {
        const overview = overviewOf(category);
        const Section = SECTIONS[category];
        const isOpen = open === category;
        return (
          <AccordionItem
            key={category}
            value={category}
            id={categoryAnchor(category)}
            data-category={category}
            className={cn(
              "scroll-mt-4 rounded-xl border bg-surface last:border-b",
              isOpen ? "border-border-strong" : "border-border",
            )}
          >
            <AccordionTrigger className="min-h-16 items-center gap-3.5 px-4.5 py-3 text-base font-normal text-foreground hover:no-underline [&>svg]:translate-y-0 [&>svg]:text-accent">
              <SeverityDot severity={overview.severity} className="size-[30px] text-lg" />
              <span className="flex min-w-0 grow flex-col">
                <span className="font-display text-2xl leading-tight font-bold">
                  {_(CATEGORY_LABELS[category])}
                </span>
                <span className="text-sm text-muted">{overview.summary}</span>
              </span>
              <span aria-hidden="true" className="shrink-0 text-[13px] text-accent">
                {_(isOpen ? LESS : MORE)}
              </span>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 px-4.5 pt-1 pb-5">
              <Section
                report={report}
                findings={report.findings.filter((finding) => finding.category === category)}
                featured={featured}
              />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
